import type { PaymentPlan, PaymentPlanItem } from '~/types/properties';

// The bot DB sometimes stores a literal string "null"/"undefined" (not a real
// null) in description fields — treat those as empty so they don't render as text.
const cleanText = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (!t || t.toLowerCase() === 'null' || t.toLowerCase() === 'undefined') return null;
  return t;
};

/**
 * Normalises the raw `payment_plans` jsonb array from the bot Postgres DB into
 * a clean, render-ready `PaymentPlan[]`.
 *
 * New DB shape (per plan):
 *   { id, name, description?, postHandover?, items: [{ id?, name, description? }] }
 *
 * The composable accepts the raw value (may be undefined / null / array) and
 * returns a filtered, normalised array — no milestone/percentage logic.
 */
export const usePropertyPaymentPlans = (
  plans: PaymentPlan[] | null | undefined,
): PaymentPlan[] => {
  if (!Array.isArray(plans) || plans.length === 0) return [];

  return plans
    .filter((plan): plan is PaymentPlan => !!plan && typeof plan.name === 'string' && plan.name.trim() !== '')
    .map((plan) => {
      const items: PaymentPlanItem[] = Array.isArray(plan.items)
        ? plan.items
            .filter((item): item is PaymentPlanItem => !!item && typeof item.name === 'string' && item.name.trim() !== '')
            .map((item) => ({
              id: item.id,
              name: item.name.trim(),
              description: cleanText(item.description),
            }))
        : [];

      return {
        id: plan.id,
        name: plan.name.trim(),
        description: cleanText(plan.description),
        postHandover: plan.postHandover ?? null,
        items,
      };
    });
};
